import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import {
  fetchEditorPresence,
  heartbeatEditorPresence,
  leaveEditorPresence,
  type EditorPresencePeer,
} from '@/services/editor-presence.service';

const TAB_ID_KEY = 'xat-painel-tab-id';

function getTabId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    const existing = window.sessionStorage.getItem(TAB_ID_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    window.sessionStorage.setItem(TAB_ID_KEY, id);
    return id;
  } catch {
    return `tab-${Date.now()}`;
  }
}

/** Resolve a chave de recurso editável a partir do path do painel (null = sem presença). */
export function resolveEditorPresenceResource(pathname: string): { resource: string; label: string } | null {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/painel/footer' || path === '/painel/identidade') {
    return { resource: 'identidade', label: 'Identidade do site' };
  }
  if (path === '/painel/paginas/homepage') return { resource: 'page:homepage', label: 'Homepage' };
  if (path === '/painel/paginas/regras') return { resource: 'page:regras', label: 'Regras' };
  if (path === '/painel/paginas/conduta') return { resource: 'page:conduta', label: 'Código de Conduta' };
  if (path === '/painel/paginas/equipe') return { resource: 'page:equipe-textos', label: 'Textos da equipe' };
  if (path === '/painel/equipe') return { resource: 'page:equipe', label: 'Equipe' };
  if (path === '/painel/coming-soon') return { resource: 'coming-soon', label: 'Coming soon' };
  if (path === '/painel/textos') return { resource: 'panel-texts', label: 'Textos do painel' };

  const guideEdit = /^\/painel\/guias\/([^/]+)\/edit$/.exec(path);
  if (guideEdit) return { resource: `guide:${decodeURIComponent(guideEdit[1])}`, label: 'Guia' };
  // Criação de novo guia/power/ace não partilha recurso — sem aviso de conflito.
  if (path === '/painel/guias/new') return null;

  const powerEdit = /^\/painel\/powers\/([^/]+)\/edit$/.exec(path);
  if (powerEdit) {
    const slug = decodeURIComponent(powerEdit[1]);
    return { resource: `power:${slug}`, label: `Power ${slug}` };
  }
  if (path === '/painel/powers/new') return null;

  const aceEdit = /^\/painel\/aces\/([^/]+)\/edit$/.exec(path);
  if (aceEdit) {
    const slug = decodeURIComponent(aceEdit[1]);
    return { resource: `ace:${slug}`, label: `Ace ${slug}` };
  }
  if (path === '/painel/aces/new') return null;

  const changelogEdit = /^\/painel\/changelog\/([^/]+)\/versions\/([^/]+)\/edit$/.exec(path);
  if (changelogEdit) {
    return {
      resource: `changelog:${changelogEdit[1]}:${decodeURIComponent(changelogEdit[2])}`,
      label: 'Changelog',
    };
  }
  const changelogNew = /^\/painel\/changelog\/([^/]+)\/versions\/novo$/.exec(path);
  if (changelogNew) {
    return { resource: `changelog:${changelogNew[1]}:new`, label: 'Novo changelog' };
  }

  return null;
}

export function useEditorPresence() {
  const location = useLocation();
  const { session, hydrated } = usePainelSession();
  const target = useMemo(
    () => resolveEditorPresenceResource(location.pathname),
    [location.pathname]
  );
  const tabId = useMemo(() => getTabId(), []);
  const [peers, setPeers] = useState<EditorPresencePeer[]>([]);
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const resourceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hydrated || !session || !target) {
      setPeers([]);
      return;
    }

    let cancelled = false;
    const { resource, label } = target;
    resourceRef.current = resource;

    const sync = async () => {
      const editors = await heartbeatEditorPresence(resource, tabId, label);
      if (!cancelled && resourceRef.current === resource) {
        setPeers(editors);
      }
    };

    const poll = async () => {
      const editors = await fetchEditorPresence(resource, tabId);
      if (!cancelled && resourceRef.current === resource) {
        setPeers(editors);
      }
    };

    void sync();
    const heartbeatTimer = window.setInterval(() => {
      void sync();
    }, 12_000);
    const pollTimer = window.setInterval(() => {
      void poll();
    }, 8_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void sync();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(heartbeatTimer);
      window.clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      void leaveEditorPresence(resource, tabId);
      if (resourceRef.current === resource) resourceRef.current = null;
    };
  }, [hydrated, session, target, tabId]);

  const activePeers = peers;
  const showWarning =
    Boolean(target) &&
    activePeers.length > 0 &&
    dismissedFor !== target?.resource;

  const dismiss = () => {
    if (target) setDismissedFor(target.resource);
  };

  return {
    resource: target?.resource ?? null,
    label: target?.label ?? null,
    peers: activePeers,
    showWarning,
    dismiss,
  };
}
